/**
 * Resend Email Service - Shared Layer
 * High-level email sending service for various email types
 * Moved to shared layer as multiple features (billing, email) need access to email sending
 */

import { resendClient } from '@/shared/services/email/resendClient';
import { generateQuoteEmail } from '@/shared/services/email/templates/quoteTemplate';
import { generateInvoiceEmail } from '@/shared/services/email/templates/invoiceTemplate';
import { generateWelcomeEmail } from '@/shared/services/email/templates/welcomeTemplate';
import { generateTeamInvitationEmail } from '@/shared/services/email/templates/teamInvitationTemplate';
import { generatePasswordResetEmail } from '@/shared/services/email/templates/passwordResetTemplate';
import {
  EmailRecipient,
  EmailOptions,
  SendEmailResponse,
  QuoteData,
  InvoiceData,
  TeamInvitationData,
  QuoteEmailOptions,
  InvoiceEmailOptions,
  WelcomeEmailOptions
} from '@/shared/types';

class ResendService {
  private client = resendClient;

  /**
   * Check if Resend is properly configured
   * @returns {boolean} True if configured
   */
  isConfigured(): boolean {
    return this.client.isConfigured();
  }

  /**
   * Send email
   * @param {string} subject - Email subject
   * @param {string} html - Email HTML content
   * @param {EmailOptions} options - Email options
   * @returns {Promise<SendEmailResponse>} Send response
   */
  async sendEmail(
    subject: string,
    html: string,
    options: EmailOptions
  ): Promise<SendEmailResponse> {
    return this.client.sendEmail(subject, html, options);
  }

  /**
   * Send quote email
   * @param {EmailRecipient} to - Recipient
   * @param {QuoteData} quoteData - Quote information
   * @param {QuoteEmailOptions} options - Additional options
   * @returns {Promise<SendEmailResponse>} Send response
   */
  async sendQuote(
    to: EmailRecipient,
    quoteData: QuoteData,
    options: Partial<QuoteEmailOptions> = {}
  ): Promise<SendEmailResponse> {
    const subject = options.subject || `Quote #${quoteData.quoteNumber} from SwaySuite`;
    const html = generateQuoteEmail(quoteData);

    const emailOptions: EmailOptions = {
      to: options.to || to,
      subject,
      html,
      tags: ['quote', `quote_${quoteData.quoteNumber}`]
    };

    return this.sendEmail(subject, html, emailOptions);
  }

  /**
   * Send invoice email
   * @param {EmailRecipient} to - Recipient
   * @param {InvoiceData} invoiceData - Invoice information
   * @param {InvoiceEmailOptions} options - Additional options
   * @returns {Promise<SendEmailResponse>} Send response
   */
  async sendInvoice(
    to: EmailRecipient,
    invoiceData: InvoiceData,
    options: Partial<InvoiceEmailOptions> = {}
  ): Promise<SendEmailResponse> {
    const subject = options.subject || `Invoice #${invoiceData.invoiceNumber} from SwaySuite`;
    const html = generateInvoiceEmail(invoiceData);

    const emailOptions: EmailOptions = {
      to: options.to || to,
      subject,
      html,
      tags: ['invoice', `invoice_${invoiceData.invoiceNumber}`]
    };

    return this.sendEmail(subject, html, emailOptions);
  }

  /**
   * Send welcome email
   * @param {EmailRecipient} to - Recipient
   * @param {string} userName - User's name
   * @param {WelcomeEmailOptions} options - Additional options
   * @returns {Promise<SendEmailResponse>} Send response
   */
  async sendWelcomeEmail(
    to: EmailRecipient,
    userName: string,
    options: Partial<WelcomeEmailOptions> = {}
  ): Promise<SendEmailResponse> {
    const subject = options.subject || 'Welcome to SwaySuite!';
    const html = generateWelcomeEmail(userName, { includeTutorial: true });

    return this.sendEmail(subject, html, {
      to: options.to || to,
      subject,
      tags: ['welcome']
    });
  }

  /**
   * Send team invitation email
   * @param {EmailRecipient} to - Recipient
   * @param {TeamInvitationData} teamData - Team invitation data
   * @returns {Promise<SendEmailResponse>} Send response
   */
  async sendTeamInvitation(
    to: EmailRecipient,
    teamData: TeamInvitationData
  ): Promise<SendEmailResponse> {
    const subject = `Invitation to join ${teamData.teamName} on SwaySuite`;
    const html = generateTeamInvitationEmail(teamData);

    return this.sendEmail(subject, html, {
      to,
      subject,
      tags: ['team_invitation']
    });
  }

  /**
   * Send password reset email
   * @param {EmailRecipient} to - Recipient
   * @param {string} resetLink - Password reset link
   * @returns {Promise<SendEmailResponse>} Send response
   */
  async sendPasswordResetEmail(
    to: EmailRecipient,
    resetLink: string
  ): Promise<SendEmailResponse> {
    const subject = 'Password Reset Request';
    const html = generatePasswordResetEmail(resetLink);

    return this.sendEmail(subject, html, {
      to,
      subject,
      tags: ['password_reset']
    });
  }
}

// Create and export singleton instance
export const resendService = new ResendService();
export default ResendService;
